import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { PRODUCT_FAMILY_OPTIONS } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const contentGateInputs = [
  {
    name: "isHardContentGate",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "isDeviceContentGate",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "productFamily",
    type: "string",
    enum: [
      { value: PRODUCT_FAMILY_OPTIONS.PLOOM_X, label: "Ploom X" },
      { value: PRODUCT_FAMILY_OPTIONS.PLOOM_S, label: "Ploom S" },
      { value: PRODUCT_FAMILY_OPTIONS.PLOOM_GEN4, label: "Ploom Gen4" },
      { value: PRODUCT_FAMILY_OPTIONS.PLOOM_GEN4AURA, label: "Ploom Gen4 Aura" },
      { value: PRODUCT_FAMILY_OPTIONS.PLOOM_GEN4CUBE, label: "Ploom Gen4 Cube" },
    ],
    required: true,
    defaultValue: PRODUCT_FAMILY_OPTIONS.PLOOM_X,
    showIf: 'options.get("isDeviceContentGate") === true',
  },
  {
    name: "deviceLimitationText",
    type: "string",
    required: true,
    defaultValue: "Do you have [ProductFamily] device to access this content?",
    showIf: 'options.get("isDeviceContentGate") === true',
  },
  {
    name: "deviceConfirmText",
    type: "string",
    required: true,
    defaultValue: "Yes, I have the device",
    showIf: 'options.get("isDeviceContentGate") === true',
  },
  {
    name: "deviceDeclineText",
    type: "string",
    required: true,
    defaultValue: "No, I don't have the device",
    showIf: 'options.get("isDeviceContentGate") === true',
  },
  {
    name: "deviceBackButtonHref",
    type: "string",
    required: false,
    defaultValue: "/",
    showIf: 'options.get("isDeviceContentGate") === true',
  },
  {
    name: "logoSrc",
    type: "file",
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F9d1d2ffe26704533a8ad5fbf1abac751",
    required: true,
  },
  {
    name: "title",
    type: "string",
    required: true,
    defaultValue: "Sorry,this content is restricted",
  },
  {
    name: "titleWhileEvaluating",
    type: "string",
    required: false,
    defaultValue: "Checking eligibility...",
  },
  {
    name: "isIPRecogintionEnabled",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "restrictionLabel",
    type: "string",
    required: true,
    defaultValue: "Redirect to a page",
    showIf: 'options.get("isIPRecogintionEnabled") === true',
  },
  {
    name: "mapCode",
    type: "string",
    required: true,
    showIf: 'options.get("isIPRecogintionEnabled") === true',
    defaultValue: "ploom-countries",
  },
  {
    name: "whyContentRestricted",
    type: "object",
    required: true,
    subFields: [
      {
        name: "label",
        type: "string",
        required: true,
      },
      {
        name: "link",
        type: "url",
        required: true,
      },
    ],
    defaultValue: {
      label: "Why is this content restricted?",
      link: "#",
    },
  },
  {
    name: "restrictions",
    type: "list",
    required: true,
    defaultValue: [
      {
        type: "DEVICE_REGISTRATION",
        label: "Register your device",
        link: "#",
      },
    ],
    showIf: 'options.get("isIPRecogintionEnabled") === false',
    subFields: [
      {
        name: "type",
        type: "string",
        enum: ["DEVICE_REGISTRATION", "LOGGED_IN", "AGE_VERIFIED", "PURPOSE"],
      },
      {
        name: "label",
        type: "string",
        required: true,
      },
      {
        name: "link",
        type: "url",
        required: true,
      },
      {
        name: "purposeId",
        type: "string",
      },
    ],
  },
] as const satisfies Inputs;

export const ContentGateComponent: RegisteredComponent = {
  name: "Content Gate",
  component: dynamic(
    async () => withValidation(contentGateInputs, (await import("./ContentGate")).ContentGate, "ContentGate"),
    { ssr: false }
  ),
  inputs: contentGateInputs,
  canHaveChildren: true,
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderComponents: true,
    builderContext: true,
  },
  defaultChildren: [],
  childRequirements: {
    message: "You can only put HealthBanner component inside this component",
    query: {
      "component.name": {
        $in: ["HealthBanner"],
      },
    },
  },
};
